import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { UUID, SuccessResponse } from './types';

export interface Footnote {
  id: UUID;
  documentSuperId: number;
}

export const footnoteQueryKeys = {
  footnotesByDocument: 'FOOTNOTES_BY_DOCUMENT',
  footnotesByTag: 'FOOTNOTES_BY_TAG',
} as const;

export function useFootnotesByDocument(documentSuperId: number | undefined): UseQueryResult<Footnote[]> {
  return useQuery(
    [footnoteQueryKeys.footnotesByDocument, documentSuperId],
    async () => ((await request.get(`/gaby/footnotes/document/${documentSuperId}`))?.data as Footnote[]) || [],
    { enabled: !!documentSuperId, onError: ServerErrorHandler }
  );
}

export function useFootnotesForTag(tagId: UUID | undefined): UseQueryResult<UUID[]> {
  return useQuery([footnoteQueryKeys.footnotesByTag, tagId], async () => ((await request.get(`/gaby/footnotes/tag/${tagId}`))?.data as UUID[]) || [], {
    enabled: !!tagId,
    onError: ServerErrorHandler,
  });
}

export function useCreateFootnote(): UseMutationResult<Footnote, unknown, { id: UUID; documentSuperId: number }> {
  const queryClient = useQueryClient();
  return useMutation(async (input) => (await request.post('/gaby/footnotes/', input))?.data as Footnote, {
    onError: ServerErrorHandler,
    onSuccess: (_d, v) => queryClient.invalidateQueries([footnoteQueryKeys.footnotesByDocument, v.documentSuperId]),
  });
}

export function useDeleteFootnote(): UseMutationResult<SuccessResponse, unknown, { id: UUID; documentSuperId: number }> {
  const queryClient = useQueryClient();
  return useMutation(async ({ id }) => (await request.del(`/gaby/footnotes/${id}`))?.data as SuccessResponse, {
    onError: ServerErrorHandler,
    onSuccess: (_d, v) => {
      queryClient.invalidateQueries([footnoteQueryKeys.footnotesByDocument, v.documentSuperId]);
      queryClient.invalidateQueries([footnoteQueryKeys.footnotesByTag]);
    },
  });
}

export function useAttachFootnote(): UseMutationResult<SuccessResponse, unknown, { footnoteId: UUID; tagId: UUID }> {
  const queryClient = useQueryClient();
  return useMutation(async ({ footnoteId, tagId }) => (await request.post(`/gaby/footnotes/${footnoteId}/tags/${tagId}`, {}))?.data as SuccessResponse, {
    onError: ServerErrorHandler,
    onSuccess: (_d, v) => queryClient.invalidateQueries([footnoteQueryKeys.footnotesByTag, v.tagId]),
  });
}

export function useDetachFootnote(): UseMutationResult<SuccessResponse, unknown, { footnoteId: UUID; tagId: UUID }> {
  const queryClient = useQueryClient();
  return useMutation(async ({ footnoteId, tagId }) => (await request.del(`/gaby/footnotes/${footnoteId}/tags/${tagId}`))?.data as SuccessResponse, {
    onError: ServerErrorHandler,
    onSuccess: (_d, v) => queryClient.invalidateQueries([footnoteQueryKeys.footnotesByTag, v.tagId]),
  });
}
